/* eslint-disable react-hooks/exhaustive-deps */
import ClearIcon from "@mui/icons-material/Clear";
import {
  IconButton,
  InputAdornment,
  OutlinedInput,
  TextFieldProps,
} from "@mui/material";
import debounce from "lodash.debounce";
import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useStore } from "zustand";
import { selectFirebaseTableFilter } from "../helpers/selectFirebaseTableFilter";
import { useFirebaseTableFilterStore } from "../hooks/useFirebaseTableFilters";

interface Props
  extends Pick<TextFieldProps, "placeholder" | "size" | "fullWidth" | "sx"> {
  filterName: string;
  hasClearButton?: boolean;
  debounceMs?: number;
}

const selectFilter = selectFirebaseTableFilter("text");

export const FirebaseTableFilterTextView: FC<Props> = (props) => {
  const { filterName, hasClearButton, debounceMs } = props;

  const filterStore = useFirebaseTableFilterStore();

  // Hook filter value - used for syncing external changes
  const hookFilterValue = useStore(
    filterStore,
    useCallback(
      selectFilter(filterName, (filter) => filter.value || ""),
      [filterName],
    ),
  );

  // Local input state - independent from hook for debounced tables
  const [localInputValue, setLocalInputValue] = useState(hookFilterValue || "");

  // Track the last hook value to detect external changes
  const lastHookValueRef = useRef(hookFilterValue);

  // Create debounced handler - only create once per component
  const debouncedSetFilterValueRef = useRef<
    ((value: string | null) => void) | null
  >(null);

  if (!debouncedSetFilterValueRef.current && debounceMs) {
    debouncedSetFilterValueRef.current = debounce(
      (value: string | null) => {
        filterStore.getState().setFilterValue("text", filterName, value);
      },
      debounceMs,
      { leading: false, trailing: true },
    );
  }

  // Sync local input with hook value when it changes externally (like URL initialization)
  useEffect(() => {
    if (debounceMs) {
      if (hookFilterValue !== lastHookValueRef.current) {
        setLocalInputValue(hookFilterValue || "");
        lastHookValueRef.current = hookFilterValue;
      }
    }
  }, [hookFilterValue, debounceMs]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value || null;

    if (debounceMs) {
      setLocalInputValue(inputValue || "");
      debouncedSetFilterValueRef.current?.(inputValue);
    } else {
      filterStore.getState().setFilterValue("text", filterName, inputValue);
    }
  };

  const handleClear = () => {
    if (debounceMs) {
      setLocalInputValue("");
      debouncedSetFilterValueRef.current?.(null);
    } else {
      filterStore.getState().setFilterValue("text", filterName, null);
    }
  };

  const displayValue = debounceMs ? localInputValue : hookFilterValue;

  return (
    <OutlinedInput
      size={props.size}
      fullWidth={props.fullWidth}
      placeholder={props.placeholder}
      value={displayValue}
      onChange={handleChange}
      sx={{
        ...(hasClearButton && { minWidth: 235 }),
        ...props.sx,
      }}
      endAdornment={
        hasClearButton &&
        displayValue && (
          <InputAdornment position="end">
            <IconButton edge="end" onClick={handleClear}>
              <ClearIcon />
            </IconButton>
          </InputAdornment>
        )
      }
    />
  );
};
